import { IPromise } from 'angular'
import moment from 'moment'
import { ApiButtonLayout, ApiResponse } from '@vend/types'

import { extractData } from '../../../util/extract-data'
import { $http } from '../../../ng-compat/imports'

interface DeletedButtonLayout {
  id: string
  deleted_at: string
}

const BUTTON_LAYOUTS_ENDPOINT = '/api/2.0/button_layouts'

/**
 * Functions for querying the Button Layouts API.
 */

/**
 * Delete a button layout. As the api return's 204 (No content) when performing a delete, this method returns a
 * a button layout object with an 'id' an a 'deleted_at' property. This ensures that when the promise is resolved,
 * we can make use of quickKeyLayoutsStore.syncData([layout]).
 *
 * @param  quickKeyLayout
 *         The layout to delete
 *
 * @return A promise resolved when the button layout has been deleted.
 */
export function deleteLayout(
  quickKeyLayout: ApiButtonLayout
): IPromise<DeletedButtonLayout> {
  return $http
    .delete<void>(`${BUTTON_LAYOUTS_ENDPOINT}/${quickKeyLayout.id}`)
    .then(() => ({
      id: quickKeyLayout.id,
      deleted_at: moment.utc().format(),
    }))
}

/**
 * Duplicate a button layout.
 *
 * @param  quickKeyLayout
 *         The layout to be duplicated
 *
 * @return A promise resolved when the button layout has been duplicated.
 */
export function duplicate(
  quickKeyLayout: ApiButtonLayout
): IPromise<ApiButtonLayout> {
  return $http
    .put<ApiResponse<ApiButtonLayout>>(
      `${BUTTON_LAYOUTS_ENDPOINT}/${quickKeyLayout.id}/actions/duplicate`,
      null
    )
    .then(extractData)
}

/**
 * Create new button layout.
 *
 * @param  layoutName
 *         The name of the new layout
 *
 * @return A promise resolved when the button layout has been created.
 */
export function create(layoutName: string): IPromise<ApiButtonLayout> {
  return $http
    .post<ApiResponse<ApiButtonLayout>>(BUTTON_LAYOUTS_ENDPOINT, {
      name: layoutName,
    })
    .then(extractData)
}

/**
 * Update the quick key button layout with the details provided.
 *
 * @param  quickKeyLayoutId
 *         The id of the quick key button layout
 *
 * @param  data
 *         The updated details of the layout
 * @return A promise resolved when the button layout has been updated.
 */
export function update(
  quickKeyLayoutId: string,
  data: ApiButtonLayout
): IPromise<ApiButtonLayout> {
  return $http
    .put<ApiResponse<ApiButtonLayout>>(
      `${BUTTON_LAYOUTS_ENDPOINT}/${quickKeyLayoutId}`,
      data
    )
    .then(extractData)
}
